import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { get, post, put } from '../../../services/apiController';
import { toast } from 'react-toastify';
import moment from 'moment';
import OrderPreview from './OrderPreview';
import { getStoredData, get_auth_key } from '../../../util';

const OrderForm = ({ productId, product, shapes, flavours, orderId, callback }) => {
  const storedData = JSON.parse(getStoredData(get_auth_key()));
  const [location, setLocation] = useState(null);
  const branchId = storedData.role === 'cake-manager' ? storedData?.locationId[0] : null;
  useEffect(() => {
    if (branchId && storedData.role === 'cake-manager') {
      get('/branches/?id=' + branchId).then((res) => {
        if (res.data.rows.length) {
          setLocation(res.data.rows[0]);
        }
      });
    }
  }, [branchId, storedData?.role]);

  const [orderAmount, setOrderAmount] = useState(0);
  const formFields = {
    weight: product?.minWeight ?? 0,
    flavourId: '',
    shapeId: '',
    nameOnCake: '',
    instruction: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    purpose: '',
    deliveryDate: '',
    advanceAmount: '',
    extraCharge: '',
    status: 'accepted',
  };
  const [form, setForm] = useState(formFields);
  const [preview, setPreview] = useState(false);

  const handleChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleFileChange = (e) => {
    e.preventDefault();
    setForm({ ...form, images: e.target.files });
  };

  const orderSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    for (const key in form) {
      if (key === 'images') {
        if (form[key] && form[key].length > 0) {
          for (let i = 0; i < form.images.length; i++) {
            formData.append('images', form.images[i]);
          }
        }
      } else {
        formData.append(key, form[key]);
      }
    }
    formData.append('productId', productId);
    formData.append('type', 'editMode');

    if (orderId) {
      put(formData, `/cake/orders/${orderId}`, false)
        .then((res) => {
          toast.success(res?.data?.message);
          callback();
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.response?.data?.data?.message || err.message));
    } else {
      post(formData, '/cake/orders', false)
        .then((res) => {
          toast.success('Orders successfully created');
          setForm(formFields);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.response?.data?.data?.message || err.message);
        });
    }
  };

  useMemo(() => {
    if (product) {
      const flavour = flavours.find((item) => item.id === +form.flavourId);
      setOrderAmount(form.weight * ((product?.price ?? 0) + (flavour?.price ?? 0)));
    }
  }, [form.weight, form.flavourId, product, flavours]);

  useEffect(() => {
    if (orderId && orderId !== '') {
      get(`/cake/orders/${orderId}`)
        .then((res) => {
          const { data } = res;
          setForm({
            weight: parseFloat(data?.weight) ?? 0,
            flavourId: data?.flavour?.id,
            shapeId: data?.shape?.id,
            nameOnCake: data?.nameOnCake,
            instruction: data?.instruction,
            customerName: data?.customerName,
            customerPhone: data?.customerPhone,
            customerEmail: data?.customerEmail,
            purpose: data?.purpose,
            deliveryDate: data?.deliveryDate,
            advanceAmount: data?.advanceAmount ?? 0,
            extraCharge: data?.extraCharge ?? 0,
            status: data?.status,
          });
        })
        .catch((err) => console.log('err', err));
    }
  }, [orderId]);

  const required = <span className="text-danger">*</span>;
  return (
    <div>
      <h6>₹ {orderAmount?.toFixed(2)}/- Amount according to weight and flavour</h6>
      <Form onSubmit={orderSubmit}>
        <Row>
          <Col className="mb-3" xs={6}>
            <Form.Label>Weight {required}</Form.Label>
            <Form.Select name="weight" value={form.weight} onChange={handleChange} className="form-select">
              <option value={0}>Select Weight</option>
              {Array.from({ length: (product?.maxWeight - product?.minWeight) * 2 + 1 }, (_, index) => {
                const weight = parseFloat((product?.minWeight + index * 0.5).toFixed(1)); // Round to 1 decimal place
                return (
                  <option key={index} value={weight}>
                    {weight}
                  </option>
                );
              })}
            </Form.Select>
          </Col>
          <Col className="mb-3" xs={6}>
            <Form.Label>Flavours {required}</Form.Label>
            <Form.Select name="flavourId" value={form.flavourId} onChange={handleChange} className="form-select">
              <option value="">Select Flavour</option>
              {flavours?.map((item, index) => (
                <option key={index} value={item?.id}>
                  {item?.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col className="mb-3" xs={6}>
            <Form.Label>Shapes {shapes.length > 0 && required}</Form.Label>
            <Form.Select name="shapeId" value={form.shapeId} onChange={handleChange} className="form-select">
              <option value="">Select Shape</option>
              {shapes?.map((item, index) => (
                <option key={index} value={item?.id}>
                  {item?.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col className="mb-3" xs={6}>
            <Form.Label>Name on Cake {required}</Form.Label>
            <Form.Control
              className="input"
              name="nameOnCake"
              onChange={handleChange}
              value={form.nameOnCake}
              type="text"
              placeholder="Enter Name on Cake"
              autoComplete="false"
            />
          </Col>
          <Col className="mb-3" xs={6}>
            <Form.Label>Special Instructions</Form.Label>
            <Form.Control
              className="input"
              name="instruction"
              value={form.instruction}
              onChange={handleChange}
              type="text"
              placeholder="Enter Special Instructions"
              autoComplete="false"
            />
          </Col>
          <Col className="mb-3" xs={6}>
            <Form.Label>Customer Name {required}</Form.Label>
            <Form.Control
              className="input"
              name="customerName"
              value={form.customerName}
              onChange={handleChange}
              type="text"
              placeholder="Enter Customer Name"
              autoComplete="false"
            />
          </Col>
          <Col className="mb-3" xs={6}>
            <Form.Label>Phone Number {required}</Form.Label>
            <Form.Control
              className="input"
              name="customerPhone"
              value={form.customerPhone}
              onChange={handleChange}
              type="tel"
              placeholder="Enter Phone Number"
              autoComplete="false"
            />
          </Col>
          <Col className="mb-3" xs={6}>
            <Form.Label>User Email</Form.Label>
            <Form.Control
              className="input"
              name="customerEmail"
              value={form.customerEmail}
              onChange={handleChange}
              type="email"
              placeholder="User Email"
              autoComplete="false"
            />
          </Col>
          <Col className="mb-3" xs={6}>
            <Form.Label>Purpose {required}</Form.Label>
            <Form.Select name="purpose" value={form.purpose} onChange={handleChange} className="form-select">
              <option value="">Select Purpose</option>
              {['Birthday', 'Anniversary', 'Others'].map((item, index) => (
                <option key={index} value={item}>
                  {item}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col className="mb-3" xs={6}>
            <Form.Label>Delivery Date {required}</Form.Label>
            <Form.Control
              className="input"
              name="deliveryDate"
              onChange={handleChange}
              value={form.deliveryDate}
              type="date"
              placeholder="Enter Delivery Date"
              autoComplete="false"
              min={moment().format('YYYY-MM-DD')}
            />
          </Col>
          <Col className="mb-3" xs={orderId ? 4 : 6}>
            <Form.Label>Advance Amount {required}</Form.Label>
            <Form.Control
              className="input"
              name="advanceAmount"
              onChange={(e) => {
                const value = e.target.value;
                if (!isNaN(value) && value >= 0) {
                  handleChange(e);
                }
              }}
              value={form.advanceAmount}
              type="text"
              placeholder="Enter Advance Amount"
              autoComplete="false"
            />
          </Col>
          <Col className="mb-3" xs={orderId ? 4 : 6}>
            <Form.Label>Extra Charges</Form.Label>
            <Form.Control
              className="input"
              name="extraCharge"
              onChange={(e) => {
                const value = e.target.value;
                if (!isNaN(value) && value >= 0) {
                  handleChange(e);
                }
              }}
              value={form.extraCharge}
              type="text"
              placeholder="Enter Extra Charge Amount"
              autoComplete="false"
            />
          </Col>
          {orderId && (
            <Col xs={4}>
              <Form.Label>Order Status</Form.Label>
              <Form.Select name="status" value={form.status} onChange={handleChange} className="form-select">
                <option value="accepted">Accepted</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Col>
          )}
          <Col xs={4}>
            <Form.Label>Images</Form.Label>
            <Form.Control
              accept="image/jpeg, image/png, image/jpg"
              className="input"
              multiple
              name="images"
              onChange={handleFileChange}
              type="file"
              autoComplete="false"
            />
          </Col>
          {location && (
            <Col xs={4}>
              <Form.Label>Delivery Location</Form.Label>
              <Form.Control
                disabled
                className="input"
                value={`${location?.name} - ${location?.number}`}
                type="text"
                placeholder="Delivery Location"
              />
            </Col>
          )}
          <Col xs={4} className="d-flex gap-2 p-3 mt-3">
            <Button type="submit" className="btn btn-danger">
              {orderId ? 'Update Order' : 'Create Order'}
            </Button>
            <Button onClick={() => setPreview(true)} className="btn btn-info">
              Preview
            </Button>
          </Col>
          <span className="text-danger mb-3">Note: Fields with (*) is required.</span>
        </Row>
      </Form>
      {preview && (
        <OrderPreview
          openModal={preview}
          closeModal={() => setPreview(false)}
          data={{ ...form, productName: product?.name }}
          images={form.images}
        />
      )}
    </div>
  );
};

export default OrderForm;
